// ===== SERVICE REGISTRY MANAGER =====
// src/utils/serviceRegistry.js
import { SERVICES } from "../config/services.js";
import { expandPermissions } from "../constants/permissions.js";

/**
 * ServiceRegistry class for managing API services and endpoints
 * Provides centralized access to service configurations, URL building,
 * and service discovery functionality
 */
export class ServiceRegistry {
  constructor(services = SERVICES) {
    this.services = services;
    this.endpointMap = new Map();
    this.resourceMap = new Map();
    this.permissionMap = new Map();

    // Build internal maps for fast lookups
    this.buildEndpointMap();
    this.buildResourceMap();
    this.buildPermissionMap();
  }

  // ===== INTERNAL MAP BUILDERS =====

  /**
   * Build internal endpoint map for fast lookups
   * Creates a flattened map of all endpoints across services
   */
  buildEndpointMap() {
    this.endpointMap.clear();

    Object.entries(this.services).forEach(([serviceKey, service]) => {
      Object.entries(service.endpoints).forEach(([endpointKey, endpoint]) => {
        const fullKey = `${serviceKey}.${endpointKey}`;

        this.endpointMap.set(fullKey, {
          ...endpoint,
          service: serviceKey,
          endpoint: endpointKey,
          fullUrl: this.buildFullUrl(service.baseUrl, endpoint.url),
          serviceName: service.name,
          resource: endpoint.resource || service.resource,
        });
      });
    });
  }

  /**
   * Build resource map grouping endpoints by resource type
   */
  buildResourceMap() {
    this.resourceMap.clear();

    this.endpointMap.forEach((endpoint, key) => {
      const resource = endpoint.resource;
      if (!resource) return;

      if (!this.resourceMap.has(resource)) {
        this.resourceMap.set(resource, []);
      }

      this.resourceMap.get(resource).push({
        key,
        ...endpoint,
      });
    });
  }

  /**
   * Build permission map grouping endpoints by required permissions
   */
  buildPermissionMap() {
    this.permissionMap.clear();

    this.endpointMap.forEach((endpoint, key) => {
      const permissions = endpoint.permissions || [];

      permissions.forEach((permission) => {
        if (!this.permissionMap.has(permission)) {
          this.permissionMap.set(permission, []);
        }

        this.permissionMap.get(permission).push({
          key,
          ...endpoint,
        });
      });
    });
  }

  // ===== ENDPOINT ACCESS METHODS =====

  /**
   * Get endpoint configuration by service and endpoint keys
   * @param {string} serviceKey - The service key (e.g., 'USERS')
   * @param {string} endpointKey - The endpoint key (e.g., 'LIST')
   * @returns {object} Endpoint configuration
   * @throws {Error} If endpoint not found
   */
  getEndpoint(serviceKey, endpointKey) {
    const fullKey = `${serviceKey}.${endpointKey}`;
    const endpoint = this.endpointMap.get(fullKey);

    if (!endpoint) {
      throw new Error(
        `Endpoint '${serviceKey}.${endpointKey}' not found in service registry. ` +
          `Available endpoints: ${Array.from(this.endpointMap.keys()).join(
            ", "
          )}`
      );
    }

    return endpoint;
  }

  /**
   * Get all endpoints for a specific service
   * @param {string} serviceKey - The service key
   * @returns {object[]} Array of endpoint configurations
   */
  getServiceEndpoints(serviceKey) {
    const service = this.services[serviceKey];
    if (!service) {
      throw new Error(`Service '${serviceKey}' not found in registry`);
    }

    return Object.entries(service.endpoints).map(([endpointKey, endpoint]) => ({
      ...endpoint,
      key: endpointKey,
      fullUrl: this.buildFullUrl(service.baseUrl, endpoint.url),
      service: serviceKey,
      serviceName: service.name,
      resource: endpoint.resource || service.resource,
    }));
  }

  /**
   * Check if an endpoint exists
   * @param {string} serviceKey - The service key
   * @param {string} endpointKey - The endpoint key
   * @returns {boolean} True if endpoint exists
   */
  hasEndpoint(serviceKey, endpointKey) {
    const fullKey = `${serviceKey}.${endpointKey}`;
    return this.endpointMap.has(fullKey);
  }

  // ===== URL BUILDING METHODS =====

  /**
   * Build full URL with parameter substitution and query params
   * @param {string} serviceKey - The service key
   * @param {string} endpointKey - The endpoint key
   * @param {object} pathParams - URL path parameters (e.g., { id: 123 })
   * @param {object} queryParams - Query string parameters
   * @returns {string} Complete URL
   */
  buildUrl(serviceKey, endpointKey, pathParams = {}, queryParams = {}) {
    const endpoint = this.getEndpoint(serviceKey, endpointKey);
    let url = endpoint.fullUrl;

    // Replace path parameters (e.g., :id -> 123)
    Object.entries(pathParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url = url.replace(`:${key}`, encodeURIComponent(String(value)));
      }
    });

    // Check for unresolved parameters
    const unresolvedParams = url.match(/:(\w+)/g);
    if (unresolvedParams) {
      console.warn(
        `Unresolved path parameters in URL '${url}': ${unresolvedParams.join(
          ", "
        )}`
      );
    }

    // Add query parameters
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes("?") ? "&" : "?") + queryString;
    }

    return url;
  }

  /**
   * Build full URL from base URL and endpoint URL
   * @param {string} baseUrl - Service base URL
   * @param {string} endpointUrl - Endpoint relative URL
   * @returns {string} Complete URL
   */
  buildFullUrl(baseUrl, endpointUrl) {
    // Remove trailing slash from baseUrl and leading slash from endpointUrl
    const cleanBase = baseUrl.replace(/\/$/, "");
    const cleanEndpoint = endpointUrl.replace(/^\//, "");

    if (!cleanEndpoint) {
      return cleanBase;
    }

    return `${cleanBase}/${cleanEndpoint}`;
  }

  // ===== DISCOVERY METHODS =====

  /**
   * Get overview of all services
   * @returns {object[]} Array of service summaries
   */
  getServicesOverview() {
    return Object.entries(this.services).map(([key, service]) => ({
      key,
      name: service.name,
      resource: service.resource,
      baseUrl: service.baseUrl,
      version: service.version,
      endpointCount: Object.keys(service.endpoints).length,
      endpoints: Object.keys(service.endpoints),
      description: service.description,
    }));
  }

  /**
   * Search endpoints by query string
   * @param {string} query - Search query
   * @returns {object[]} Matching endpoints
   */
  searchEndpoints(query) {
    const results = [];
    const searchTerm = query.toLowerCase();

    this.endpointMap.forEach((endpoint, key) => {
      const searchableText = [
        endpoint.description,
        endpoint.method,
        endpoint.fullUrl,
        key,
        endpoint.service,
        endpoint.resource,
      ]
        .join(" ")
        .toLowerCase();

      if (searchableText.includes(searchTerm)) {
        results.push({ key, ...endpoint });
      }
    });

    return results.sort((a, b) => {
      // Sort by relevance (exact matches first)
      const aExact = a.key.toLowerCase().includes(searchTerm);
      const bExact = b.key.toLowerCase().includes(searchTerm);

      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      return a.key.localeCompare(b.key);
    });
  }

  /**
   * Get endpoints by required permission
   * @param {string} permission - Permission string
   * @returns {object[]} Endpoints requiring the permission
   */
  getEndpointsByPermission(permission) {
    return this.permissionMap.get(permission) || [];
  }

  /**
   * Get endpoints by resource type
   * @param {string} resource - Resource name
   * @returns {object[]} Endpoints for the resource
   */
  getEndpointsByResource(resource) {
    return this.resourceMap.get(resource) || [];
  }

  /**
   * Get all unique resources
   * @returns {string[]} Array of resource names
   */
  getAllResources() {
    return Array.from(this.resourceMap.keys()).sort();
  }

  /**
   * Get all unique permissions from endpoints
   * @returns {string[]} Array of permissions
   */
  getAllPermissions() {
    return Array.from(this.permissionMap.keys()).sort();
  }

  /**
   * Get permissions for a specific resource
   * @param {string} resource - Resource name
   * @returns {string[]} Array of permissions
   */
  getResourcePermissions(resource) {
    const endpoints = this.getEndpointsByResource(resource);
    const permissions = new Set();

    endpoints.forEach((endpoint) => {
      if (endpoint.permissions) {
        endpoint.permissions.forEach((permission) =>
          permissions.add(permission)
        );
      }
    });

    return Array.from(permissions).sort();
  }

  /**
   * Get available actions for a resource based on user permissions
   * @param {string} resource - Resource name
   * @param {string[]} userPermissions - User's permissions
   * @returns {object[]} Available actions
   */
  getAvailableActions(resource, userPermissions = []) {
    const endpoints = this.getEndpointsByResource(resource);
    const expandedPermissions = expandPermissions(userPermissions);
    const availableActions = [];

    endpoints.forEach((endpoint) => {
      if (!endpoint.permissions || endpoint.permissions.length === 0) {
        // No permissions required
        availableActions.push({
          action: endpoint.action || endpoint.method.toLowerCase(),
          method: endpoint.method,
          endpoint: endpoint.endpoint,
          description: endpoint.description,
        });
      } else {
        // Check if user has all required permissions
        const hasAllPermissions = endpoint.permissions.every((permission) =>
          expandedPermissions.includes(permission)
        );

        if (hasAllPermissions) {
          availableActions.push({
            action: endpoint.action || endpoint.method.toLowerCase(),
            method: endpoint.method,
            endpoint: endpoint.endpoint,
            description: endpoint.description,
            permissions: endpoint.permissions,
          });
        }
      }
    });

    return availableActions;
  }

  // ===== VALIDATION METHODS =====

  /**
   * Validate endpoint configuration
   * @param {string} serviceKey - The service key
   * @param {string} endpointKey - The endpoint key
   * @returns {object} Validation result
   */
  validateEndpoint(serviceKey, endpointKey) {
    try {
      const endpoint = this.getEndpoint(serviceKey, endpointKey);
      const issues = [];

      // Check required fields
      if (!endpoint.description) {
        issues.push("Missing description");
      }

      if (!endpoint.method) {
        issues.push("Missing HTTP method");
      }

      // Validate HTTP method
      const validMethods = [
        "GET",
        "POST",
        "PUT",
        "DELETE",
        "PATCH",
        "HEAD",
        "OPTIONS",
      ];
      if (endpoint.method && !validMethods.includes(endpoint.method)) {
        issues.push(`Invalid HTTP method: ${endpoint.method}`);
      }

      // Check URL parameters match endpoint params definition
      const urlParams = (endpoint.url.match(/:(\w+)/g) || []).map((p) =>
        p.substring(1)
      );
      const definedParams = endpoint.params || [];

      urlParams.forEach((param) => {
        if (!definedParams.includes(param)) {
          issues.push(`URL parameter :${param} not defined in params array`);
        }
      });

      // Check for unused defined parameters
      definedParams.forEach((param) => {
        if (!urlParams.includes(param)) {
          issues.push(`Defined parameter '${param}' not used in URL`);
        }
      });

      // Validate permissions format
      if (endpoint.permissions) {
        endpoint.permissions.forEach((permission) => {
          if (!/^[a-z]+:[a-z_]+$/.test(permission)) {
            issues.push(
              `Invalid permission format: '${permission}' (should be 'resource:action')`
            );
          }
        });
      }

      // Validate cache configuration
      if (endpoint.cache) {
        if (endpoint.cache.enabled && !endpoint.cache.ttl) {
          issues.push("Cache enabled but TTL not specified");
        }
      }

      // Method-specific validations
      if (endpoint.method !== "GET" && endpoint.polling?.enabled) {
        issues.push("Polling should only be enabled for GET requests");
      }

      return {
        valid: issues.length === 0,
        issues,
        endpoint: endpoint.key,
        service: endpoint.service,
      };
    } catch (error) {
      return {
        valid: false,
        issues: [error.message],
        endpoint: endpointKey,
        service: serviceKey,
      };
    }
  }

  /**
   * Validate all endpoints in the registry
   * @returns {object} Comprehensive validation results
   */
  validateAllEndpoints() {
    const results = {
      totalEndpoints: 0,
      validEndpoints: 0,
      invalidEndpoints: 0,
      services: {},
      issues: [],
    };

    Object.keys(this.services).forEach((serviceKey) => {
      const serviceEndpoints = this.getServiceEndpoints(serviceKey);
      results.services[serviceKey] = {
        name: this.services[serviceKey].name,
        total: serviceEndpoints.length,
        valid: 0,
        invalid: 0,
        endpoints: {},
      };

      serviceEndpoints.forEach((endpoint) => {
        results.totalEndpoints++;

        const validation = this.validateEndpoint(serviceKey, endpoint.key);
        results.services[serviceKey].endpoints[endpoint.key] = validation;

        if (validation.valid) {
          results.validEndpoints++;
          results.services[serviceKey].valid++;
        } else {
          results.invalidEndpoints++;
          results.services[serviceKey].invalid++;
          results.issues.push({
            service: serviceKey,
            endpoint: endpoint.key,
            issues: validation.issues,
          });
        }
      });
    });

    return results;
  }

  // ===== DOCUMENTATION GENERATION =====

  /**
   * Generate comprehensive API documentation
   * @returns {object} Complete API documentation
   */
  generateDocumentation() {
    const docs = {
      title: "API Documentation",
      version: "1.0.0",
      generatedAt: new Date().toISOString(),
      baseUrl: process.env.REACT_APP_API_BASE_URL,
      totalServices: Object.keys(this.services).length,
      totalEndpoints: this.endpointMap.size,
      services: {},
      summary: {
        resources: this.getAllResources(),
        permissions: this.getAllPermissions(),
        httpMethods: this.getUsedHttpMethods(),
      },
    };

    Object.entries(this.services).forEach(([serviceKey, service]) => {
      docs.services[serviceKey] = {
        name: service.name,
        description: service.description,
        resource: service.resource,
        baseUrl: service.baseUrl,
        version: service.version,
        endpoints: {},
      };

      Object.entries(service.endpoints).forEach(([endpointKey, endpoint]) => {
        docs.services[serviceKey].endpoints[endpointKey] = {
          method: endpoint.method,
          url: this.buildFullUrl(service.baseUrl, endpoint.url),
          description: endpoint.description,
          parameters: {
            path: endpoint.params || [],
            query: endpoint.queryParams || [],
            body: endpoint.bodySchema || null,
          },
          response: endpoint.responseSchema || null,
          authentication: {
            required: endpoint.requiresAuth || false,
            permissions: endpoint.permissions || [],
          },
          caching: endpoint.cache || null,
          rateLimit: endpoint.rateLimit || null,
          features: {
            optimistic: endpoint.optimistic || false,
            polling: endpoint.polling || null,
            confirmation: endpoint.confirmation || null,
          },
        };
      });
    });

    return docs;
  }

  /**
   * Get all HTTP methods used across endpoints
   * @returns {string[]} Array of HTTP methods
   */
  getUsedHttpMethods() {
    const methods = new Set();
    this.endpointMap.forEach((endpoint) => {
      methods.add(endpoint.method);
    });
    return Array.from(methods).sort();
  }

  /**
   * Generate resource-permission matrix
   * @returns {object} Matrix showing resources and their permissions
   */
  generateResourcePermissionMatrix() {
    const matrix = {};

    this.getAllResources().forEach((resource) => {
      const endpoints = this.getEndpointsByResource(resource);
      const permissions = this.getResourcePermissions(resource);

      matrix[resource] = {
        endpoints: endpoints.map((ep) => ({
          key: ep.endpoint,
          method: ep.method,
          description: ep.description,
          permissions: ep.permissions || [],
        })),
        permissions: permissions,
        endpointCount: endpoints.length,
        permissionCount: permissions.length,
      };
    });

    return matrix;
  }

  // ===== UTILITY METHODS =====

  /**
   * Get service statistics
   * @returns {object} Registry statistics
   */
  getStatistics() {
    const methodCounts = {};
    const resourceCounts = {};
    const permissionCounts = {};

    this.endpointMap.forEach((endpoint) => {
      // Count methods
      methodCounts[endpoint.method] = (methodCounts[endpoint.method] || 0) + 1;

      // Count resources
      if (endpoint.resource) {
        resourceCounts[endpoint.resource] =
          (resourceCounts[endpoint.resource] || 0) + 1;
      }

      // Count permissions
      if (endpoint.permissions) {
        endpoint.permissions.forEach((permission) => {
          permissionCounts[permission] =
            (permissionCounts[permission] || 0) + 1;
        });
      }
    });

    return {
      services: Object.keys(this.services).length,
      endpoints: this.endpointMap.size,
      resources: this.resourceMap.size,
      permissions: this.permissionMap.size,
      methodDistribution: methodCounts,
      resourceDistribution: resourceCounts,
      permissionUsage: Object.entries(permissionCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10) // Top 10 most used permissions
        .reduce((acc, [perm, count]) => ({ ...acc, [perm]: count }), {}),
    };
  }

  /**
   * Export registry data for external use
   * @returns {object} Exportable registry data
   */
  exportData() {
    return {
      services: this.services,
      endpoints: Object.fromEntries(this.endpointMap),
      resources: Object.fromEntries(this.resourceMap),
      permissions: Object.fromEntries(this.permissionMap),
      statistics: this.getStatistics(),
      documentation: this.generateDocumentation(),
    };
  }
}

// Create and export the singleton instance
export const serviceRegistry = new ServiceRegistry(SERVICES);

// Export utility functions that work with the singleton
export const getEndpoint = (serviceKey, endpointKey) =>
  serviceRegistry.getEndpoint(serviceKey, endpointKey);

export const buildUrl = (serviceKey, endpointKey, pathParams, queryParams) =>
  serviceRegistry.buildUrl(serviceKey, endpointKey, pathParams, queryParams);

export const hasEndpoint = (serviceKey, endpointKey) =>
  serviceRegistry.hasEndpoint(serviceKey, endpointKey);

export const searchEndpoints = (query) =>
  serviceRegistry.searchEndpoints(query);

// Make available globally in development
if (process.env.NODE_ENV === "development") {
  window.serviceRegistry = serviceRegistry;
}
